import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { App, DatePicker, Space } from 'antd'
import BrandButton from '@/components/BrandButton'
import type { AppVersion } from '@/types'
import { CardTitle, SectionCard } from './parts'
import timerIcon from './images/timer-icon.svg'
import {
  disabledScheduleDate,
  disabledScheduleTime,
  formatSchedule,
  isScheduleAtLeastOneHourAhead,
  parseSchedule,
  scheduleIsoFromPicker,
} from './scheduleTime'

function GoLiveText({ time }: { time: string }) {
  const { t } = useTranslation()
  const label = t('versionForm.willReleaseAt', { time })
  const idx = label.indexOf(time)
  if (idx < 0) return <>{label}</>
  return (
    <>
      {label.slice(0, idx)}
      <span style={{ fontWeight: 500 }}>{time}</span>
      {label.slice(idx + time.length)}
    </>
  )
}

/** 「待上线」状态的操作区（取消定时 / 修改时间 / 立即上线）— 对齐 Figma 10051:175705 */
export default function ScheduledOpsBar({
  version,
  onCancel,
  onReleaseNow,
  onModify,
}: {
  version: AppVersion
  onCancel: () => void
  onReleaseNow: () => void
  onModify: (iso: string) => Promise<void>
}) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [value, setValue] = useState(() => parseSchedule(version.scheduled_at))

  useEffect(() => {
    setValue(parseSchedule(version.scheduled_at))
  }, [version.scheduled_at])

  const timeLabel = formatSchedule(version.scheduled_at)

  const confirmModify = async () => {
    const iso = scheduleIsoFromPicker(value)
    if (!iso) return
    if (!isScheduleAtLeastOneHourAhead(iso)) {
      message.error(t('versionForm.errScheduleTime'))
      return
    }
    setSaving(true)
    try {
      await onModify(iso)
      setEditing(false)
    } catch {
      // 错误提示由调用方负责
    } finally {
      setSaving(false)
    }
  }

  return (
    <SectionCard>
      <CardTitle>{t('versionForm.releaseSchedule')}</CardTitle>

      <div
        style={{
          padding: '0 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 14,
              lineHeight: '22px',
            }}
          >
            <span style={{ color: '#000' }}>V {version.version}</span>
            <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
              {t('versions.status_scheduled')}
            </span>
          </div>
          <div style={{ fontSize: 14, lineHeight: '22px', color: '#000' }}>
            <GoLiveText time={timeLabel} />
          </div>
        </div>

        {editing ? (
          <Space wrap>
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              showNow={false}
              value={value}
              disabled={saving}
              disabledDate={(d) => disabledScheduleDate(d)}
              disabledTime={(d) => disabledScheduleTime(d)}
              onChange={setValue}
            />
            <BrandButton disabled={saving} loading={saving} onClick={() => void confirmModify()}>
              {t('common.confirm')}
            </BrandButton>
            <BrandButton
              variant="outline"
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              {t('common.cancel')}
            </BrandButton>
          </Space>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <BrandButton variant="outline" onClick={onCancel}>
              {t('versions.action_cancelSchedule')}
            </BrandButton>
            <BrandButton
              variant="outline"
              icon={<img src={timerIcon} alt="" width={16} height={16} />}
              onClick={() => setEditing(true)}
            >
              {t('versionForm.modifyTime')}
            </BrandButton>
            <BrandButton onClick={onReleaseNow}>
              {t('versions.action_releaseNow')}
            </BrandButton>
          </div>
        )}
      </div>
    </SectionCard>
  )
}
